import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toZonedTime } from 'date-fns-tz';
import { getDate, getHours } from 'date-fns';

import { useSelectorUserInfo } from '@/components/auth/AuthContext';
import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import ButtonCustom from '@/components/common/ui/button';
import { useTranslation } from '@/config/i18n';
import { useUnivaPay } from '@/hooks';
import {
  normalizeTimeZone,
  toastErrorMessage,
  toastMessage,
} from '@/lib/utils';
import { PlanItem } from '@/pages/register-account/components';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { GraduationType } from '@/types/graduation';
import { PlanType } from '@/types/plan';
import ModalConfirm from './ModalConfirm';
import { ROLE, STAGE_GRADUATION_STATUS } from '@/shared/constants/user';
import { PAYMENT_TYPE, PAYMENT_TYPE_TEXT } from '@/shared/constants/payment';

const ChangePlanForm = () => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const navigate = useNavigate();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const { startCheckout } = useUnivaPay();
  const { currentUser } = useSelectorUserInfo();
  const {
    plan: currentPlan,
    graduation,
    email,
    phone_number,
    current_roles,
    stage_graduation_status,
    payment_type,
  } = currentUser || {};
  const userRole = current_roles?.[0] || '';
  const isCoachTrial =
    userRole === ROLE.TEACHER &&
    stage_graduation_status === STAGE_GRADUATION_STATUS.TRIAL;
  const isCoachFree =
    userRole === ROLE.TEACHER &&
    stage_graduation_status === STAGE_GRADUATION_STATUS.FREE;

  const [plans, setPlans] = useState<PlanType[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>();
  const [errors, setErrors] = useState<string>('');
  const [visibleConfirmUpgradePlan, setVisibleConfirmUpgradePlan] =
    useState(false);
  const [visibleConfirmDowngradePlan, setVisibleConfirmDowngradePlan] =
    useState(false);
  const [verifySuccess, setVerifySuccess] = useState(false);

  const isSelectedPlanHigherThanMinePlan = useMemo(() => {
    if (!currentPlan || !selectedPlan) return false;

    return selectedPlan.price > currentPlan.price;
  }, [currentPlan, selectedPlan]);

  const isDisabledChangePlan = useMemo(
    () =>
      !selectedPlan ||
      currentPlan?.id === selectedPlan.id ||
      (currentPlan?.price === selectedPlan.price &&
        currentPlan?.student_limit === selectedPlan.student_limit),
    [currentPlan, selectedPlan],
  );

  const getPlanList = useCallback(async () => {
    setLoadingGlobal(true);
    const { data } = await apiService.guest.collection('plans', {
      method: 'GET',
      params: {
        timezone: normalizeTimeZone(
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        ),
      },
    });

    setLoadingGlobal(false);
    setPlans(data?.data || []);
  }, []);

  const onOpenModalConfirmUpgradePlan = () => {
    setVisibleConfirmUpgradePlan(true);
  };

  const onOpenModalConfirmDowngradePlan = () => {
    setVisibleConfirmDowngradePlan(true);
  };

  const handleNavigateBack = () => {
    navigate(PATH.profile);
  };

  function handleErrorDowngradePlan(error: any) {
    const { error_code } = error;
    if (error_code === 'MFS-E-LEARNING-ADMIN-DOWNGRADE-PLAN-4') {
      toast?.current?.show(
        toastErrorMessage(t('profile.change_plan.cannot_downgrade_upgrade')),
      );
      return;
    }

    if (error_code === 'MFS-E-LEARNING-ADMIN-INVALID-TIMEFRAME-UP-OR-DOWN') {
      toast?.current?.show(
        toastErrorMessage(t('profile.change_plan.cannot_change_plan_when_wrong_time')),
      );
      return;
    }

    toast?.current?.show(
      toastErrorMessage(t('profile.change_plan.downgrade_fail')),
    );
  }

  function handleErrorUpgradePlan(error: any) {
    const { error_code } = error;
    if (error_code === 'MFS-E-LEARNING-ADMIN-UPGRADE-PLAN-6') {
      toast?.current?.show(
        toastErrorMessage(t('profile.change_plan.cannot_downgrade_upgrade')),
      );
      return;
    }

    if (error_code === 'MFS-E-LEARNING-ADMIN-INVALID-TIMEFRAME-UP-OR-DOWN') {
      toast?.current?.show(
        toastErrorMessage(t('profile.change_plan.cannot_change_plan_when_wrong_time')),
      );
      return;
    }

    toast?.current?.show(
      toastErrorMessage(t('profile.change_plan.upgrade_fail')),
    );
  }

  const isRestrictedUpgradeTime = (): boolean => {
    const JAPAN_TIMEZONE = 'Asia/Tokyo';
    const now = new Date();

    const japanTime = toZonedTime(now, JAPAN_TIMEZONE);

    const isFirstDayOfMonth = getDate(japanTime) === 1;
    const hour = getHours(japanTime);

    return isFirstDayOfMonth && hour < 10;
  }

  const handleChangePlan = async () => {
    if (!currentPlan) return;

    if (!selectedPlan) {
      setErrors(t('profile.change_plan.plan_required'));
      return;
    }

    if (isRestrictedUpgradeTime()) {
      toast?.current?.show(toastErrorMessage(t('profile.change_plan.cannot_change_plan_when_wrong_time')));
      return;
    }

    if (isSelectedPlanHigherThanMinePlan) {
      onOpenModalConfirmUpgradePlan();
      return;
    }

    onOpenModalConfirmDowngradePlan();
  };

  const handleDowngradePlan = async () => {
    try {
      setLoadingGlobal(true);

      const { error } = await apiService.users.collection('plans/downgrade', {
        method: 'POST',
        body: {
          plan_id: selectedPlan?.id,
        },
      });

      if (error) {
        handleErrorDowngradePlan(error);
        return;
      }

      setVerifySuccess(true);
      toast?.current?.show(
        toastMessage(t('profile.change_plan.downgrade_success')),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingGlobal(false);
      setVisibleConfirmDowngradePlan(false);
    }
  };

  const handleRecurringPlan = async () => {
    const { data, error } = await apiService.users.collection(
      'plans/recurring-upgrade',
      {
        method: 'POST',
        body: {
          plan_id: selectedPlan?.id,
        },
      },
    );

    if (error) {
      toast?.current?.show(toastErrorMessage(error.message));
      return;
    }

    if (data) {
      setVerifySuccess(true);
      toast?.current?.show(
        toastMessage(t('profile.change_plan.upgrade_success')),
      );
    }
  };

  const handleSubscriptionPlan = async () => {
    const { data: dataResponse, error } = await apiService.users.collection(
      'plans/upgrade/request',
      {
        method: 'POST',
        body: {
          plan_id: selectedPlan?.id,
        },
      },
    );

    if (error) {
      handleErrorUpgradePlan(error);
      return;
    }

    const {
      data: { key, is_charge_fee },
    } = dataResponse;

    const subscriptionInitialAmount = is_charge_fee
      ? Number(selectedPlan?.price) - Number(currentPlan?.price || 0)
      : 0;
    setTimeout(() => {
      startCheckout(
        PAYMENT_TYPE_TEXT.PLAN,
        selectedPlan as PlanType,
        graduation as GraduationType,
        email,
        phone_number as string,
        key,
        async (subscriptionId: string) => {
          const { data, error } = await apiService.users.collection(
            'plans/upgrade/complete',
            {
              method: 'POST',
              body: {
                key,
                new_subscription_id: subscriptionId,
              },
            },
          );

          if (error) {
            toast?.current?.show(toastErrorMessage(error.message));
            return;
          }

          if (data) {
            setVerifySuccess(true);
            toast?.current?.show(
              toastMessage(t('profile.change_plan.upgrade_success')),
            );
          }
        },
        subscriptionInitialAmount.toString(),
      );
    }, 500);
  };

  const handleUpgradePlan = async () => {
    try {
      setLoadingGlobal(true);

      if (payment_type === PAYMENT_TYPE.RECURRING) {
        await handleRecurringPlan();
        return;
      }

      await handleSubscriptionPlan();
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingGlobal(false);
      setVisibleConfirmUpgradePlan(false);
    }
  };

  useEffect(() => {
    getPlanList();
  }, [getPlanList]);

  useEffect(() => {
    if (selectedPlan) {
      setErrors('');
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (verifySuccess) {
      setTimeout(() => {
        window.location.href = PATH.profile;
      }, 500);
    }
  }, [verifySuccess]);

  useEffect(() => {
    if (currentPlan) {
      const currentPlanInPlans = plans?.find(
        (plan) => plan.id === currentPlan.id,
      );
      if (currentPlanInPlans) {
        setSelectedPlan(currentPlanInPlans);
      }
    }
  }, [currentPlan, plans]);

  useEffect(() => {
    if (isCoachTrial || isCoachFree) {
      navigate(PATH.forbidden);
    }
  }, []);

  return (
    <div>
      <div className="max-w-[900px] flex flex-col gap-4 mt-6">
        {plans.map((plan, index) => (
          <PlanItem
            key={index}
            plan={plan}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
          />
        ))}
        {errors && <div className="text-error-500 mt-2">{errors}</div>}
      </div>
      <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px] gap-5">
        <ButtonCustom
          className="w-[140px]"
          type="button"
          severity="secondary"
          onClick={handleNavigateBack}
        >
          {t('profile.change_plan.btn_cancel')}
        </ButtonCustom>
        <ButtonCustom
          className="w-[140px]"
          type="button"
          onClick={handleChangePlan}
          disabled={isDisabledChangePlan}
        >
          {t('profile.change_plan.btn_submit')}
        </ButtonCustom>
      </div>

      <ModalConfirm
        dialogConfirmEditVisible={visibleConfirmDowngradePlan}
        setDialogConfirmEditVisible={setVisibleConfirmDowngradePlan}
        title={'プランの変更確認'}
        loadingGlobal={loadingGlobal}
        content={
          <>
            <p className="text-center text-gray-900">
              ご指定のプランにダウングレードします。
              <br />
              プラン変更により、翌月料金は
              <span className="text-error-600">
                {Number(selectedPlan?.price).toLocaleString()}
              </span>
              円になります。
            </p>
          </>
        }
        submit={handleDowngradePlan}
      />

      <ModalConfirm
        dialogConfirmEditVisible={visibleConfirmUpgradePlan}
        setDialogConfirmEditVisible={setVisibleConfirmUpgradePlan}
        title={'プランの変更確認'}
        loadingGlobal={loadingGlobal}
        content={
          <>
            <p className="text-center text-gray-900">
              ご指定のプランにアップグレードします。
              <br />
              プランアップグレードに伴い、差額として
              <span className="text-error-600">
                {`${(Number(selectedPlan?.price) - Number(currentPlan?.price || 0)).toLocaleString()}`}
              </span>
              円が発生しますのでご了承ください。
            </p>
          </>
        }
        submit={handleUpgradePlan}
      />
    </div>
  );
};

export default ChangePlanForm;
